import { useState } from "react";
import { Box, Paper, Typography, useMediaQuery } from "@mui/material";
import {
  Datagrid,
  FunctionField,
  List,
  ReferenceInput,
  SelectField,
  SelectInput,
  SimpleList,
  TextField,
  TextInput,
  Title,
  useNotify,
  useStore,
} from "react-admin";
import CustomFilter from "features/CustomFilter";
import { MobileListRow } from "features/MobileListRow";
import UserField from "server/components/Fields/UserField";
import { debtsRequests } from "api/debtsApi";
import { CurrencyChoices, DebtStatusChoices, UserRoleChoices } from "utils/choices";
import { MOBILE_BREAKPOINT, UserRoles } from "utils/constants";
import permissions from "app/permissions";
import SecuredComponent from "server/security/SecuredComponent";
import { settings } from "app";
import TransactionsDialog from "./TransactionsDialog";
import AddPaymentDialog from "./AddPaymentDialog";

export const OBLIGATIONS_STORE_KEY = "obligations.filters";

// Amounts are shown together with the debt currency, e.g. "120.00 EUR"
const getAmountText = (amount, currency) =>
  `${Number(amount ?? 0).toFixed(2)} ${CurrencyChoices.find(c => c.id === currency)?.name ?? ""}`.trim();

const getDebtAmountText = record => getAmountText(record?.amount, record?.currency);

const getPaidAmountText = record => getAmountText(record?.paidAmount, record?.currency);

const ListObligations = () => {
  const isMobile = useMediaQuery(MOBILE_BREAKPOINT);
  const notify = useNotify();

  const [filters, setFilters] = useStore(OBLIGATIONS_STORE_KEY, null);

  const [userRole, setUserRole] = useState(filters?.UserRole ?? null);

  const searchParams = () => {
    if (!userRole) {
      notify("Моля изберете роля на потребителя!", { type: "error" });
      return;
    }

    setFilters({ UserRole: userRole });
  };

  const hasFacultyNumber = [UserRoles.Student, UserRoles.Doctorant].includes(filters?.UserRole);

  const listFilters = [
    ...(hasFacultyNumber
      ? [
          <TextInput
            source="FacultyNumber"
            label="Факултетен номер"
            alwaysOn
          />,
        ]
      : []),
    <TextInput
      source="PersonId"
      label="ЕГН / ЛНЧ"
      alwaysOn
    />,
    <TextInput
      source="FirstName"
      label="Име"
      alwaysOn
    />,
    <TextInput
      source="SecondName"
      label="Презиме"
      alwaysOn
    />,
    <TextInput
      source="LastName"
      label="Фамилия"
      alwaysOn
    />,
    <ReferenceInput
      source="DebtType"
      reference={debtsRequests.getDebtTypesSelect()}
      alwaysOn
    >
      <SelectInput
        label="Вид задължение"
        optionText="nameBg"
        emptyText="Всички"
        style={{ width: 220 }}
      />
    </ReferenceInput>,
    <SelectInput
      source="DebtStatus"
      label="Статус"
      choices={DebtStatusChoices}
      alwaysOn
      emptyText="Всички"
      style={{ width: 200 }}
    />,
  ];

  return (
    <>
      {!filters && <Title title="Задължения" />}
      <CustomFilter
        title="Моля изберете роля за да видите списъка със задължения"
        filters={[
          {
            label: "Роля",
            value: userRole,
            setValue: setUserRole,
            type: "select",
            choices: UserRoleChoices,
          },
        ]}
        cardWidth={isMobile ? "350px" : "700px"}
        titleClasses={{
          minWidth: isMobile ? "250px" : "550px",
          maxWidth: "min-content",
          marginLeft: "auto",
          marginRight: "auto",
        }}
        buttonLabel="Провери"
        buttonOnClick={searchParams}
      />

      {!filters ? (
        <Paper>
          <Box mt={3}>
            <Typography
              variant="inherit"
              align="center"
              color={"secondary"}
            >
              Въведи данни за търсене
            </Typography>
          </Box>
        </Paper>
      ) : (
        <List
          title="Задължения"
          storeKey={`obligations.list.${filters.UserRole}`}
          filters={listFilters}
          filter={filters}
          exporter={false}
        >
          {isMobile ? (
            <SimpleList
              linkType={false}
              sx={settings.style.mobileList}
              primaryText={record => (
                <div>
                  <MobileListRow fields={[{ label: "Име", value: <UserField source="userId" /> }]} />
                  <MobileListRow fields={[{ label: "Вид задължение", value: record?.debtTypeName }]} />
                </div>
              )}
              secondaryText={record => (
                <div>
                  <MobileListRow
                    fields={[
                      { label: "Сума", value: getDebtAmountText(record) },
                      { label: "Платена сума", value: getPaidAmountText(record) },
                    ]}
                  />
                  <MobileListRow
                    fields={[
                      {
                        label: "Статус",
                        value: (
                          <SelectField
                            source="debtStatus"
                            choices={DebtStatusChoices}
                          />
                        ),
                      },
                    ]}
                  />
                </div>
              )}
              tertiaryText={() => (
                <>
                  <TransactionsDialog />
                  <SecuredComponent permission={permissions.canAddDebtPayment}>
                    <AddPaymentDialog />
                  </SecuredComponent>
                </>
              )}
            />
          ) : (
            <Datagrid
              bulkActionButtons={false}
              rowClick={false}
            >
              <UserField
                label="Име"
                source="userId"
                sortable={false}
              />
              <TextField
                source="debtTypeName"
                label="Вид задължение"
                sortable={false}
              />
              <FunctionField
                label="Сума"
                render={getDebtAmountText}
                sortable={false}
              />
              <FunctionField
                label="Платена сума"
                render={getPaidAmountText}
                sortable={false}
              />
              <SelectField
                source="debtStatus"
                label="Статус"
                choices={DebtStatusChoices}
                sortable={false}
              />
              <TransactionsDialog />
              <SecuredComponent permission={permissions.canAddDebtPayment}>
                <AddPaymentDialog />
              </SecuredComponent>
            </Datagrid>
          )}
        </List>
      )}
    </>
  );
};

export default ListObligations;
